import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import './RemplissageAtelier.css';

// ═══════════════════════════════════════════════════════════════════════════
// Tableaux ▸ Remplissage atelier — V1, super-admin.
//
// Décompose, jour par jour et atelier par atelier, tout ce qui entre dans un
// taux de remplissage : effectif, amplitude, absences, heures planifiées, RDV
// non honorés. Aucune couleur, aucun seuil : c'est un écran de VALIDATION des
// paramètres avec les chefs d'atelier, pas encore un indicateur publié. Les
// définitions vivent dans le serveur (services/remplissage-atelier.js) et sont
// reprises en clair au bas de la page.
// ═══════════════════════════════════════════════════════════════════════════

const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Bornes de la période contenant la date d'ancre (semaine = lundi → dimanche).
function bornesPeriode(periode, ancre) {
  const d = new Date(ancre.getFullYear(), ancre.getMonth(), ancre.getDate());
  if (periode === 'semaine') {
    const lundi = new Date(d); lundi.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const dimanche = new Date(lundi); dimanche.setDate(lundi.getDate() + 6);
    return { du: toYmd(lundi), au: toYmd(dimanche) };
  }
  return {
    du: toYmd(new Date(d.getFullYear(), d.getMonth(), 1)),
    au: toYmd(new Date(d.getFullYear(), d.getMonth() + 1, 0)),
  };
}

const fmtJour = (ymd) => {
  const [a, m, j] = ymd.split('-').map(Number);
  return new Date(a, m - 1, j).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });
};
const fmtJourCourt = (ymd) => {
  const [a, m, j] = ymd.split('-').map(Number);
  return new Date(a, m - 1, j).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};
const fmtH = (x) => (x === null || x === undefined ? '—' : x.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
const fmtPct = (x) => (x === null || x === undefined ? '—' : `${Math.round(x * 100)} %`);

const COLONNES = [
  { cle: 'mecaniciens', label: 'Mécaniciens', type: 'n', aide: 'Mécaniciens actifs rattachés à l’atelier ce jour (dates d’entrée / sortie respectées).' },
  { cle: 'presents', label: 'Présents', type: 'n', aide: 'Mécaniciens sans absence sur la journée entière.' },
  { cle: 'theoriques', label: 'Théoriques', type: 'h', aide: 'Mécaniciens × amplitude nette de l’atelier (ouverture → fermeture, pauses déduites). 0 si l’atelier est fermé ce jour ou férié.' },
  { cle: 'absences', label: 'Absences', type: 'h', aide: 'Heures d’absence (congés, maladie, formation…). Les « indisponibilités » ne comptent pas : le mécanicien est là. Absences partielles au prorata.' },
  { cle: 'disponibles', label: 'Disponibles', type: 'h', aide: 'Théoriques − absences.' },
  { cle: 'planifiees', label: 'Planifiées', type: 'h', aide: 'Somme des durées des cartes du jour affectées à un mécanicien de l’atelier, compléments inclus, hors « à planifier ». Les RDV non honorés en font partie : ils occupaient le planning.' },
  { cle: 'non_affectees', label: 'Non affectées', type: 'h', total: true, aide: 'Cartes du jour sans mécanicien, ou dont le mécanicien n’est rattaché à aucun atelier actif. Hors des taux.' },
  { cle: 'non_honorees', label: 'Non honorées', type: 'h', aide: 'Durée des cartes marquées « RDV non honoré ». Retirées dans le taux réel.' },
  { cle: 'vides', label: 'Vides', type: 'h', aide: 'Disponibles − planifiées. Négatif = surbooking, affiché tel quel.' },
  { cle: 'taux_theorique', label: 'Taux / théo.', type: 'p', aide: 'Planifiées / théoriques.' },
  { cle: 'taux_disponible', label: 'Taux / dispo.', type: 'p', aide: 'Planifiées / disponibles.' },
  { cle: 'taux_reel', label: 'Taux réel', type: 'p', aide: '(Planifiées − non honorées) / disponibles.' },
];
const fmt = (col, v) => (col.type === 'p' ? fmtPct(v) : col.type === 'h' ? fmtH(v) : (v ?? '—'));

export default function RemplissageAtelier() {
  const [periode, setPeriode] = useState('semaine'); // 'semaine' | 'mois'
  const [ancre, setAncre] = useState(() => new Date());
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [exportEnCours, setExportEnCours] = useState(false);

  const { du, au } = bornesPeriode(periode, ancre);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur('');
    try {
      const { data } = await api.get(`/planning/remplissage?du=${du}&au=${au}`);
      setDonnees(data);
    } catch (e) {
      console.error('[remplissage]', e);
      setErreur(e.response?.status === 403 ? 'Réservé aux super-administrateurs.' : (e.response?.data?.error || 'Chargement impossible'));
      setDonnees(null);
    } finally {
      setChargement(false);
    }
  }, [du, au]);
  useEffect(() => { charger(); }, [charger]);

  const naviguer = (sens) => {
    const d = new Date(ancre);
    if (periode === 'semaine') d.setDate(d.getDate() + 7 * sens); else d.setMonth(d.getMonth() + sens);
    setAncre(d);
  };
  const aujourdHui = toYmd(new Date());
  const surAujourdHui = du <= aujourdHui && aujourdHui <= au;
  const libellePeriode = periode === 'semaine'
    ? `du ${fmtJourCourt(du)} au ${fmtJourCourt(au)}`
    : new Date(ancre.getFullYear(), ancre.getMonth(), 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

  const exporterCsv = async () => {
    setExportEnCours(true);
    try {
      const r = await api.get(`/planning/remplissage?du=${du}&au=${au}&format=csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `remplissage_${du}_${au}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setErreur('Export impossible');
    } finally {
      setExportEnCours(false);
    }
  };

  const jours = donnees?.jours || [];
  const cumul = donnees?.cumul;

  return (
    <div className="rmp">
      <div className="rmp-entete">
        <div>
          <h2>📊 Remplissage atelier</h2>
          <p className="rmp-sous">
            Chaque brique du taux, jour par jour, pour valider les paramètres avec les chefs d'atelier.
            Calcul à la volée sur le planning actuel : une journée passée reflète le planning tel qu'il est <em>aujourd'hui</em>.
          </p>
        </div>
        <div className="rmp-outils">
          <div className="rmp-filtres">
            {[['semaine', 'Semaine'], ['mois', 'Mois']].map(([cle, lbl]) => (
              <button key={cle} type="button" className={`rmp-filtre${periode === cle ? ' is-active' : ''}`}
                onClick={() => setPeriode(cle)}>{lbl}</button>
            ))}
          </div>
          <div className="rmp-nav">
            <button type="button" className="rmp-nav-btn" onClick={() => naviguer(-1)} title="Période précédente">◀</button>
            <span className="rmp-nav-label">{libellePeriode}</span>
            <button type="button" className="rmp-nav-btn" onClick={() => naviguer(1)} title="Période suivante">▶</button>
            {!surAujourdHui && (
              <button type="button" className="rmp-nav-today" onClick={() => setAncre(new Date())}>Aujourd'hui</button>
            )}
          </div>
          <button type="button" className="rmp-export" onClick={exporterCsv} disabled={exportEnCours || !donnees}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {exportEnCours ? 'Export…' : 'CSV'}
          </button>
        </div>
      </div>

      {erreur && <p className="rmp-note">⚠️ {erreur}</p>}
      {chargement && !donnees && <p className="rmp-note">Chargement…</p>}
      {(donnees?.avertissements || []).map((a, i) => <p key={i} className="rmp-note rmp-avert">⚠️ {a}</p>)}

      {/* ── Cumul de la période : la ligne qu'on relit d'une semaine à l'autre ── */}
      {cumul && (
        <div className="table-container">
          <table className="rmp-table">
            <thead>
              <tr>
                <th>Cumul période — {donnees.concession.nom}</th>
                <th className="rmp-num" title="Jours où l’atelier est ouvert sur la période">Jours ouv.</th>
                {COLONNES.filter(c => !['mecaniciens', 'presents'].includes(c.cle)).map(c => (
                  <th key={c.cle} className="rmp-num" title={c.aide}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cumul.ateliers.map(a => (
                <tr key={a.atelier_id}>
                  <td className="rmp-nom">{a.atelier}</td>
                  <td className="rmp-num">{a.jours_ouverts}</td>
                  {COLONNES.filter(c => !['mecaniciens', 'presents'].includes(c.cle)).map(c => (
                    <td key={c.cle} className={`rmp-num${c.type === 'p' ? ' rmp-pct' : ''}`}>{c.total ? '' : fmt(c, a[c.cle])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="rmp-pied">
                <td>Total</td>
                <td className="rmp-num">{cumul.total.jours_ouverts}</td>
                {COLONNES.filter(c => !['mecaniciens', 'presents'].includes(c.cle)).map(c => (
                  <td key={c.cle} className={`rmp-num${c.type === 'p' ? ' rmp-pct' : ''}`}>{fmt(c, cumul.total[c.cle])}</td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* ── Détail jour × atelier ── */}
      {jours.length > 0 && (
        <div className="table-container">
          <table className="rmp-table rmp-detail">
            <thead>
              <tr>
                <th>Date</th>
                <th>Atelier</th>
                <th className="rmp-num" title="Ouverture → fermeture, pauses déduites">Ampl.</th>
                {COLONNES.map(c => <th key={c.cle} className="rmp-num" title={c.aide}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {jours.map(j => {
                const ouverts = j.lignes.filter(l => l.ouvert);
                const t = j.total;
                if (ouverts.length === 0) {
                  // Journée sans aucun atelier ouvert : une seule ligne. Des heures
                  // planifiées sur un jour fermé sont une information, pas un bug.
                  return (
                    <tr key={j.jour} className="rmp-ferme">
                      <td className="rmp-date">{fmtJour(j.jour)}</td>
                      <td colSpan={2 + COLONNES.length}>
                        {j.ferie ? 'Férié' : 'Fermé'}
                        {t.planifiees > 0 && <> — {fmtH(t.planifiees)} h planifiées malgré tout</>}
                        {t.non_affectees > 0 && <> ({fmtH(t.non_affectees)} h non affectées)</>}
                      </td>
                    </tr>
                  );
                }
                return (
                  <React.Fragment key={j.jour}>
                    {ouverts.map((l, i) => (
                      <tr key={l.atelier_id}>
                        {i === 0 && <td className="rmp-date" rowSpan={ouverts.length + 1}>{fmtJour(j.jour)}</td>}
                        <td className="rmp-nom">{l.atelier}</td>
                        <td className="rmp-num rmp-muted">{fmtH(l.amplitude)}</td>
                        {COLONNES.map(c => (
                          <td key={c.cle} className={`rmp-num${c.type === 'p' ? ' rmp-pct' : ''}${c.cle === 'vides' && l.vides < 0 ? ' rmp-neg' : ''}`}>
                            {c.total ? '' : fmt(c, l[c.cle])}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="rmp-total">
                      <td className="rmp-nom">Total</td>
                      <td className="rmp-num"></td>
                      {COLONNES.map(c => (
                        <td key={c.cle} className={`rmp-num${c.type === 'p' ? ' rmp-pct' : ''}${c.cle === 'vides' && t.vides < 0 ? ' rmp-neg' : ''}`}>
                          {c.cle === 'mecaniciens' || c.cle === 'presents'
                            ? ouverts.reduce((s, l) => s + l[c.cle], 0)
                            : fmt(c, t[c.cle])}
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {donnees && (
        <details className="rmp-defs">
          <summary>Définitions des colonnes et paramètres lus</summary>
          <dl>
            <dt>Ampl.</dt><dd>Ouverture → fermeture de l'atelier, pauses du jour déduites. 0 si fermé ce jour (jours d'ouverture de l'atelier) ou férié (territoire de la concession).</dd>
            {COLONNES.map(c => <React.Fragment key={c.cle}><dt>{c.label}</dt><dd>{c.aide}</dd></React.Fragment>)}
          </dl>
          <p className="rmp-note">Ateliers lus : {donnees.ateliers.map(a => (
            `${a.nom} (${String(a.heure_ouverture || '').slice(0, 5)}–${String(a.heure_fermeture || '').slice(0, 5)}, jours ${(a.jours_ouverture || []).join(',') || 'par défaut 1-6'}, ${a.mecaniciens.length} mécanicien${a.mecaniciens.length > 1 ? 's' : ''})`
          )).join(' · ')}.</p>
        </details>
      )}
    </div>
  );
}
